import { z } from 'zod'
import { role } from './role'
import { paginatedSearchQuery } from '../zod/pagination'
import { locale } from './locale'
import { permission, Permission } from './permission'
import { Company, company } from '../company/company'
import { invitation } from '../invitation/invitation'
import { avatarConfig } from './input/update-input'

export const user = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    role,
    locale,
    avatarConfig,
    avatar: z.string(),
    permissions: z.array(permission).optional(),
    companies: z
        .object({
            id: z.string(),
            name: z.string(),
        })
        .array()
        .optional(),
    activeCompany: company.optional().nullable(),
    emailVerifiedAt: z.coerce.date().nullable(),
    invitations: invitation.array().optional(),
    blockedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
})

export type User = z.infer<typeof user>

export const userQuery = paginatedSearchQuery({
    filters: z.object({
        name: z.string().array().optional(),
        email: z.string().array().optional(),
        blocked: z.boolean().array().optional(),
        role: role.array().optional(),
    }),
    sort: z.enum(['name', '-name', 'email', '-email', 'role', '-role']),
})

export type UserQuery = z.infer<typeof userQuery>

export function can(user?: User, permission: Permission | Permission[] = []): boolean {
    if (Array.isArray(permission)) {
        return permission.filter((permission) => user?.permissions?.includes(permission)).length === permission.length
    }

    return user?.permissions?.includes(permission) || false
}

export function canAny(user?: User, permissions: Permission[] = []): boolean {
    return permissions.filter((permission) => user?.permissions?.includes(permission)).length > 0
}

/**
 * The Website plan is about the website first: its home is the websites, not the recruitment dashboard. Recruitment is in
 * every plan; on the Website plan every candidate is paid for.
 */
export function isWebsitePlan(company?: Company | null) {
    return company?.tier === 'website'
}

export function hasSubscription(company?: Company | null) {
    if (!company) {
        return false
    }

    return !!company.tier
}
